import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styles from "./UserSettings.module.css";
import { ButtonBack, ButtonSave } from "../../components/ui";
import api from "../../services/api";

export default function UserSettings() {
  // ─── Profile section state ────────────────────────────────────────────────
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState(false);

  // ─── Password section state ───────────────────────────────────────────────
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // ─── Load profile on mount ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/auth/me");
        setProfile({ name: res.data.name || "", email: res.data.email || "" });
      } catch {
        // Fall back to localStorage if /auth/me fails
        try {
          const stored = localStorage.getItem("user");
          if (stored) {
            const u = JSON.parse(stored);
            setProfile({ name: u.name || "", email: u.email || "" });
          }
        } catch {
          // nothing
        }
      } finally {
        setProfileLoading(false);
      }
    };
    load();
  }, []);

  // ─── Auto-clear success banners ───────────────────────────────────────────
  useEffect(() => {
    if (profileSuccess) {
      const t = setTimeout(() => setProfileSuccess(false), 4000);
      return () => clearTimeout(t);
    }
  }, [profileSuccess]);

  useEffect(() => {
    if (passwordSuccess) {
      const t = setTimeout(() => setPasswordSuccess(false), 4000);
      return () => clearTimeout(t);
    }
  }, [passwordSuccess]);

  // ─── Profile handlers ─────────────────────────────────────────────────────
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess(false);

    if (!profile.name.trim() || !profile.email.trim()) {
      setProfileError("Nome e e-mail são obrigatórios.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!emailRegex.test(profile.email)) {
      setProfileError("Informe um e-mail válido.");
      return;
    }

    setProfileSaving(true);
    try {
      const res = await api.patch("/auth/me", {
        name: profile.name.trim(),
        email: profile.email.trim(),
      });
      // Update localStorage cache
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        localStorage.setItem(
          "user",
          JSON.stringify({ ...u, name: res.data.user.name, email: res.data.user.email }),
        );
      }
      setProfileSuccess(true);
      toast.success("Perfil atualizado com sucesso!");
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setProfileError("Este e-mail já está em uso por outra conta.");
      } else {
        setProfileError("Erro ao salvar. Tente novamente.");
      }
    } finally {
      setProfileSaving(false);
    }
  };

  // ─── Password handlers ────────────────────────────────────────────────────
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordBlur = (field) =>
    setPasswordTouched((prev) => ({ ...prev, [field]: true }));

  const passwordNewTooShort =
    passwords.newPassword.length > 0 && passwords.newPassword.length < 8;
  const passwordsMismatch =
    passwords.confirmPassword.length > 0 &&
    passwords.newPassword !== passwords.confirmPassword;

  const isPasswordValid =
    passwords.currentPassword.length > 0 &&
    passwords.newPassword.length >= 8 &&
    passwords.newPassword === passwords.confirmPassword;

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordTouched({
      currentPassword: true,
      newPassword: true,
      confirmPassword: true,
    });
    setPasswordError("");
    setPasswordSuccess(false);
    if (!isPasswordValid) return;

    setPasswordSaving(true);
    try {
      await api.patch("/auth/me", {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordTouched({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
      setPasswordSuccess(true);
      toast.success("Senha alterada com sucesso!");
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setPasswordError("A senha atual está incorreta. Verifique e tente novamente.");
      } else {
        setPasswordError("Erro ao alterar a senha. Tente novamente.");
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <header className={styles.header}>
        <ButtonBack to="/agenda" label="← Agenda" />
        <div className={styles.headerText}>
          <h1 className={styles.pageTitle}>Configurações do usuário</h1>
          <p className={styles.pageSubtitle}>
            Gerencie seus dados pessoais e credenciais de acesso.
          </p>
        </div>
      </header>

      <div className={styles.main}>
        {profileLoading ? (
          <div className={styles.skeleton} aria-busy="true" aria-label="Carregando..." />
        ) : (
          <>
            {/* ── Section 1 — Dados Pessoais ─────────────────────────────── */}
            <section className={styles.section} aria-labelledby="profile-title">
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIconWrap} aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div>
                  <h2 id="profile-title" className={styles.sectionTitle}>
                    Dados pessoais
                  </h2>
                  <p className={styles.sectionSubtitle}>
                    Atualize seu nome e endereço de e-mail.
                  </p>
                </div>
              </div>

              {profileError && (
                <div className={styles.alertError} role="alert" aria-live="polite">
                  ❌ {profileError}
                </div>
              )}
              {profileSuccess && (
                <div className={styles.alertSuccess} role="status" aria-live="polite">
                  ✅ Dados salvos com sucesso!
                </div>
              )}

              <form
                className={styles.form}
                noValidate
                onSubmit={handleProfileSubmit}
              >
                <div className={styles.formGrid}>
                  <div className={styles.field}>
                    <label htmlFor="name" className={styles.label}>
                      Nome <span className={styles.required} aria-hidden="true">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      className={styles.input}
                      value={profile.name}
                      onChange={handleProfileChange}
                      autoComplete="name"
                      disabled={profileSaving}
                      placeholder="Seu nome completo"
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="email" className={styles.label}>
                      E-mail <span className={styles.required} aria-hidden="true">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className={styles.input}
                      value={profile.email}
                      onChange={handleProfileChange}
                      autoComplete="email"
                      disabled={profileSaving}
                      placeholder="voce@exemplo.com"
                    />
                  </div>
                </div>

                <div className={styles.formActions}>
                  <ButtonSave
                    type="submit"
                    isLoading={profileSaving}
                    loadingText="Salvando..."
                  >
                    Salvar alterações
                  </ButtonSave>
                </div>
              </form>
            </section>

            {/* ── Divider ──────────────────────────────────────────────────── */}
            <hr className={styles.divider} />

            {/* ── Section 2 — Alterar Senha ──────────────────────────────── */}
            <section className={styles.section} aria-labelledby="password-title">
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIconWrap} aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </div>
                <div>
                  <h2 id="password-title" className={styles.sectionTitle}>
                    Alterar senha
                  </h2>
                  <p className={styles.sectionSubtitle}>
                    Escolha uma senha segura com no mínimo 8 caracteres.
                  </p>
                </div>
              </div>

              {passwordError && (
                <div className={styles.alertError} role="alert" aria-live="polite">
                  ❌ {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className={styles.alertSuccess} role="status" aria-live="polite">
                  ✅ Senha alterada com sucesso!
                </div>
              )}

              <form
                className={styles.form}
                noValidate
                onSubmit={handlePasswordSubmit}
              >
                {/* Senha atual */}
                <div className={styles.field}>
                  <label htmlFor="currentPassword" className={styles.label}>
                    Senha atual <span className={styles.required} aria-hidden="true">*</span>
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="currentPassword"
                      name="currentPassword"
                      type={showCurrentPass ? "text" : "password"}
                      className={`${styles.input} ${
                        passwordTouched.currentPassword &&
                        !passwords.currentPassword
                          ? styles.inputError
                          : ""
                      }`}
                      value={passwords.currentPassword}
                      onChange={handlePasswordChange}
                      onBlur={() => handlePasswordBlur("currentPassword")}
                      autoComplete="current-password"
                      disabled={passwordSaving}
                      placeholder="Sua senha atual"
                    />
                    <button
                      type="button"
                      className={styles.showBtn}
                      onClick={() => setShowCurrentPass((v) => !v)}
                      aria-label={showCurrentPass ? "Ocultar senha atual" : "Mostrar senha atual"}
                      disabled={passwordSaving}
                    >
                      {showCurrentPass ? "Ocultar" : "Mostrar"}
                    </button>
                  </div>
                  {passwordTouched.currentPassword && !passwords.currentPassword && (
                    <span className={styles.errorText}>
                      Informe sua senha atual.
                    </span>
                  )}
                </div>

                {/* Nova senha */}
                <div className={styles.field}>
                  <label htmlFor="newPassword" className={styles.label}>
                    Nova senha <span className={styles.required} aria-hidden="true">*</span>
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPass ? "text" : "password"}
                      className={`${styles.input} ${
                        passwordTouched.newPassword && passwordNewTooShort
                          ? styles.inputError
                          : ""
                      }`}
                      value={passwords.newPassword}
                      onChange={handlePasswordChange}
                      onBlur={() => handlePasswordBlur("newPassword")}
                      autoComplete="new-password"
                      disabled={passwordSaving}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button
                      type="button"
                      className={styles.showBtn}
                      onClick={() => setShowNewPass((v) => !v)}
                      aria-label={showNewPass ? "Ocultar nova senha" : "Mostrar nova senha"}
                      disabled={passwordSaving}
                    >
                      {showNewPass ? "Ocultar" : "Mostrar"}
                    </button>
                  </div>
                  {passwordTouched.newPassword && passwordNewTooShort && (
                    <span className={styles.errorText}>
                      A nova senha deve ter pelo menos 8 caracteres.
                    </span>
                  )}
                </div>

                {/* Confirmar nova senha */}
                <div className={styles.field}>
                  <label htmlFor="confirmPassword" className={styles.label}>
                    Confirmar nova senha <span className={styles.required} aria-hidden="true">*</span>
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPass ? "text" : "password"}
                      className={`${styles.input} ${
                        passwordTouched.confirmPassword && passwordsMismatch
                          ? styles.inputError
                          : ""
                      }`}
                      value={passwords.confirmPassword}
                      onChange={handlePasswordChange}
                      onBlur={() => handlePasswordBlur("confirmPassword")}
                      autoComplete="new-password"
                      disabled={passwordSaving}
                      placeholder="Repita a nova senha"
                    />
                    <button
                      type="button"
                      className={styles.showBtn}
                      onClick={() => setShowConfirmPass((v) => !v)}
                      aria-label={showConfirmPass ? "Ocultar confirmação" : "Mostrar confirmação"}
                      disabled={passwordSaving}
                    >
                      {showConfirmPass ? "Ocultar" : "Mostrar"}
                    </button>
                  </div>
                  {passwordTouched.confirmPassword && passwordsMismatch && (
                    <span className={styles.errorText}>
                      As senhas não coincidem.
                    </span>
                  )}
                </div>

                <div className={styles.formActions}>
                  <ButtonSave
                    type="submit"
                    isLoading={passwordSaving}
                    loadingText="Alterando..."
                    disabled={!isPasswordValid && !passwordSaving}
                  >
                    Alterar senha
                  </ButtonSave>
                </div>
              </form>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
